import React, { useState, useEffect } from 'react';
import { 
  CpuChipIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

/**
 * Widget per mostrare lo stato delle licenze nella pagina rete multilivello
 */
const LicenseStatusWidget = ({ userRole, userId }) => {
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      loadLicenseStatus();
    }
  }, [userId]);

  const loadLicenseStatus = async () => {
    try {
      setLoading(true);
      
      // Admin non ha vincoli di licenze
      if (userRole === 'admin') {
        setLicenseStatus({
          isAdmin: true,
          message: 'Admin: Nessun vincolo di licenze'
        });
        setLoading(false);
        return;
      }

      // Carica dashboard licenze per l'agenzia
      const response = await fetch('/api/licenses/dashboard/auto', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Trova la licenza agente per vedere i seats disponibili
        const agentLicense = data.data.licenze.find(lic => lic.licenza.codice === 'LICENZA_AGENTE');
        
        setLicenseStatus({
          isAdmin: false,
          agentLicense: agentLicense,
          totalLicenses: data.data.licenze.length,
          canCreateAgents: agentLicense && agentLicense.seatsInfo && agentLicense.seatsInfo.disponibili > 0
        });
      } else {
        setLicenseStatus({
          error: 'Errore caricamento licenze'
        });
      }
    } catch (error) {
      console.error('Errore caricamento stato licenze:', error);
      setLicenseStatus({
        error: 'Errore caricamento licenze'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="animate-pulse flex space-x-3">
          <div className="rounded-full bg-gray-300 h-8 w-8"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="h-3 bg-gray-300 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!licenseStatus) {
    return null;
  }

  if (licenseStatus.error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
          <span className="text-red-700 text-sm">{licenseStatus.error}</span>
        </div>
      </div>
    );
  }

  if (licenseStatus.isAdmin) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <CpuChipIcon className="h-6 w-6 text-blue-600" />
          <div>
            <h4 className="font-medium text-blue-900">Modalità Admin</h4>
            <p className="text-blue-700 text-sm">Puoi creare utenti senza vincoli di licenze</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <CpuChipIcon className="h-5 w-5 text-gray-600" />
          <span className="font-medium text-gray-900">Stato Licenze</span>
        </div>
        <a 
          href="/nlt/gestione-licenze" 
          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          Gestisci →
        </a>
      </div>

      <div className="space-y-2">
        {/* Licenze Agente */}
        {licenseStatus.agentLicense ? (
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-2">
              {licenseStatus.canCreateAgents ? (
                <CheckCircleIcon className="h-4 w-4 text-green-500" />
              ) : (
                <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
              )}
              <span>Licenze Agente</span>
            </div>
            <span className={`font-medium ${
              licenseStatus.canCreateAgents ? 'text-green-600' : 'text-red-600'
            }`}>
              {licenseStatus.agentLicense.seatsInfo.disponibili}/{licenseStatus.agentLicense.seatsInfo.totale} disponibili
            </span>
          </div>
        ) : (
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
              <span>Licenze Agente</span>
            </div>
            <span className="text-red-600 font-medium">Non disponibili</span>
          </div>
        )}

        {/* Totale licenze */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2">
            <EyeIcon className="h-4 w-4 text-gray-400" />
            <span>Licenze Totali</span>
          </div>
          <span className="text-gray-600 font-medium">{licenseStatus.totalLicenses}</span>
        </div>
      </div>

      {/* Alert se non può creare agenti */}
      {!licenseStatus.canCreateAgents && licenseStatus.agentLicense && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800 text-xs">
            ⚠️ Nessun seat disponibile per creare nuovi agenti. 
            <a href="/nlt/gestione-licenze" className="underline hover:text-yellow-900">
              Acquista più licenze
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

export default LicenseStatusWidget;
